import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { getPortalMenuItemWithPermissionsCountFx } from '@nx-frontend/shared/store/feature-menu-tree';
import { $hasCoupons } from '@nx-frontend/shared/store/org-coupons';
import { $orgInfoSubscriptionType } from '@nx-frontend/shared/store/org-info';
import { $orgRole } from '@nx-frontend/shared/store/org-role';
import { $orgUsageData } from '@nx-frontend/shared/store/org-usage';
import {
  ManageMembersType,
  ManageMembersView,
  MenuItemType,
  OrgInfoSubscriptionType,
  OrgMemberRole,
  Permissions,
  PermissionsAccessMode,
  PopupType,
} from '@nx-frontend/shared/types';
import { getNodeNameByType } from '@nx-frontend/shared/utils';
import '../dots-animation.scss';
import { ClientsLimitMessage } from '../messages/clients-limit-message';
import { GuestsLimitMessage } from '../messages/guests-limit-message';
import { MembersLimitMessage } from '../messages/members-limit-message';
import { PortalNodeClientsInfoMessage } from '../messages/portal-node-clients-info-message';
import {
  $clientsDisabled,
  $context,
  $hasBillingInfo,
  $forceIsOnlySharedAvailable,
  $manageMembersType,
  $manageMembersWorkspaceMemberUserIds,
  $menuItem,
  $orgMemberEmails,
  $portalClientAccess,
  $portalConsoleAccess,
  $showUpgradeMessage,
  $workspaceMemberEmails,
  DraftInviteCount,
  getManageMembersDataFx,
  initialDraftInviteCount,
  inviteToPortalFx,
  OrgMemberRoleForInvite,
  updateDraftInviteCountEvent,
  updateMenuItemEvent,
  updatePermissionsFx,
  updateViewModeEvent,
} from '../store';
import { getPortalClientOrgRolesFromAccess, getPortalConsoleOrgRolesFromAccess } from '../utils';
import { InviteItemModel, Item } from './invite-item';

interface Invite {
  email: string;
  role: string;
  orgRole?: OrgMemberRole;
}
export function InviteList({
  onUpdateMenuItemPermissions,
}: {
  onUpdateMenuItemPermissions?: (id: string, permissions: Permissions) => void;
}) {
  const [
    manageMembersType,
    menuItem,
    context,
    workspaceMemberUserIds,
    orgMemberEmails,
    workspaceMemberEmails,
    portalConsoleAccess,
    portalClientAccess,
    showUpgradeMessage,
    orgRole,
    orgUsage,
    hasBillingInfo,
    hasCoupons,
    orgInfoSubscriptionType,
    clientsDisabled,
    forceIsOnlySharedAvailable,
  ] = useUnit([
    $manageMembersType,
    $menuItem,
    $context,
    $manageMembersWorkspaceMemberUserIds,
    $orgMemberEmails,
    $workspaceMemberEmails,
    $portalConsoleAccess,
    $portalClientAccess,
    $showUpgradeMessage,
    $orgRole,
    $orgUsageData,
    $hasBillingInfo,
    $hasCoupons,
    $orgInfoSubscriptionType,
    $clientsDisabled,
    $forceIsOnlySharedAvailable,
  ]);
  const defaultRole = clientsDisabled ? OrgMemberRole.MEMBER : OrgMemberRole.CLIENT;
  const defaultAccessRole =
    manageMembersType === ManageMembersType.PORTAL ? PermissionsAccessMode.FULL_ACCESS : undefined;

  const [
    updateViewMode,
    updatePermissions,
    updateMenuItem,
    closeModal,
    inviteToPortal,
    getManageMembersData,
    updateDraftInviteCount,
    getPortalMenuItemWithPermissionsCount,
    openModal,
  ] = useEvent([
    updateViewModeEvent,
    updatePermissionsFx,
    updateMenuItemEvent,
    closeModalEvent,
    inviteToPortalFx,
    getManageMembersDataFx,
    updateDraftInviteCountEvent,
    getPortalMenuItemWithPermissionsCountFx,
    openModalEvent,
  ]);
  const t = useTranslations('features');
  const { toast } = useToast();
  const title = menuItem?.name ?? '';
  const type = menuItem?.type ?? MenuItemType.UNKNOWN;
  const [items, setItems] = useState<InviteItemModel[]>([getInitialItem(0, defaultRole, defaultAccessRole)]);
  const [isOnlySharedAvailable, setIsOnlySharedAvailable] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const availableConsoleOrgAccessRoles = getPortalConsoleOrgRolesFromAccess(portalConsoleAccess);
  const availablePortalAccessRoles = getPortalClientOrgRolesFromAccess(portalClientAccess);

  const sendDisabled = items.some((item) => item.isError) || showUpgradeMessage || isSending;

  const draftInviteCount = items.reduce(
    (acc, item) => {
      if (item.email.trim().length) {
        acc[item.role]++;
      }
      return acc;
    },
    { ...initialDraftInviteCount } as DraftInviteCount
  );

  useEffect(() => {
    if (forceIsOnlySharedAvailable) {
      setIsOnlySharedAvailable(true);
      return;
    }

    (async () => {
      const count = await getPortalMenuItemWithPermissionsCount();
      if (count) {
        setIsOnlySharedAvailable(true);
      }
    })();
  }, [getPortalMenuItemWithPermissionsCount, forceIsOnlySharedAvailable]);

  useEffect(() => {
    updateDraftInviteCount(draftInviteCount);
  }, [draftInviteCount, updateDraftInviteCount]);

  const membersCurrent = orgUsage.members.current + draftInviteCount.member + draftInviteCount.manager;
  const membersMax = orgUsage.members.max;
  const membersLimit = membersCurrent > membersMax;

  const clientsCurrent = orgUsage.clients.current + draftInviteCount.client;
  const clientsMax = orgUsage.clients.max;
  const clientsLimit = clientsCurrent > clientsMax;

  const guestsCurrent = orgUsage.guests.current + +draftInviteCount.guest;
  const guestsMax = orgUsage.guests.max;
  const guestsLimit = guestsCurrent > guestsMax;

  const showLimitError = orgRole === OrgMemberRole.OWNER && hasBillingInfo && !showUpgradeMessage;

  const showClientsError =
    clientsLimit &&
    showLimitError &&
    (orgInfoSubscriptionType === OrgInfoSubscriptionType.PLUS ||
      orgInfoSubscriptionType === OrgInfoSubscriptionType.TEAM ||
      orgInfoSubscriptionType === OrgInfoSubscriptionType.TEAM2024 ||
      orgInfoSubscriptionType === OrgInfoSubscriptionType.PLUS2024);

  const showMembersError = membersLimit && showLimitError && !showClientsError;
  const showGuestsError = guestsLimit && showLimitError;

  const [lastAction, setLastAction] = useState<'add' | 'remove' | null>(null);

  const handleAddItem = () => {
    setLastAction('add');
    setItems([...items, getInitialItem(items.length, defaultRole, defaultAccessRole)]);
  };

  const handleRemoveItem = (idToRemove: string) => {
    setLastAction('remove');
    if (items.length > 1) {
      const updatedItems = items.filter((item) => item.id !== idToRemove);
      setItems(updatedItems);
    }
  };

  function transformInputStrings(type: MenuItemType): string {
    if (
      type === MenuItemType.HOME ||
      type === MenuItemType.NOTE ||
      type === MenuItemType.PORTAL_PAGE ||
      type === MenuItemType.ALL_PAGES
    ) {
      return 'page';
    } else if (type === MenuItemType.LINK) {
      return 'link';
    } else if (type === MenuItemType.NOTES_FOLDER || type === MenuItemType.PORTAL_SECTION) {
      return 'folder';
    }
    return '';
  }

  const handleUpdateItem = (index: number, newItem: InviteItemModel) => {
    const updatedItems = [...items];
    const updatedItem = { ...newItem };
    if (
      !(isOnlySharedAvailable && !availableConsoleOrgAccessRoles.includes(newItem.role)) &&
      updatedItem.accessMode === PermissionsAccessMode.SHARED_ACCESS
    ) {
      updatedItem.accessMode = PermissionsAccessMode.FULL_ACCESS;
    }
    updatedItems[index] = updatedItem;
    setItems(updatedItems);
  };

  const showNotOwnerLimitError = () => {
    openModal({
      title: 'This Plan is Now Full 😔',
      content: PopupType.DIALOG,
      closeButtonSize: 0,
      payload: {
        payloadType: PopupType.DIALOG,
        text: (
          <div style={{ maxWidth: '470px' }}>
            The organization has reached the member limit. Please contact the organization owner for further details.
          </div>
        ),
        buttons: [
          {
            main: true,
            text: 'Got it',
            size: ButtonSize.MD,
          },
        ],
      },
    });
  };

  const showAppsumoLimitPopup = () => {
    openModal({
      content: PopupType.DIALOG,
      showCloseButton: false,
      payload: {
        payloadType: PopupType.DIALOG,
        text: (
          <div>
            <img className="-mt-4 pb-4" src="/assets/img/popup-invite.png" alt="Your request has been accepted" />
            <h2 className="font-semibold text-xl">Your request has been accepted</h2>
            <p className="py-2.5 text-base">
              You've reached the limits. To add more members, click the <br /> corresponding button to express your
              interest, and our support team <br />
              will get in touch with you via email.
            </p>
          </div>
        ),
        buttons: [
          {
            main: false,
            text: 'Cancel',
            size: ButtonSize.MD,
          },
          {
            main: true,
            text: 'Submit request',
            size: ButtonSize.MD,
            onClick: () => {
              window.open('https://share.hsforms.com/1GU-hPq0aRq-Xkz9ftUXt2wcsi4h', '_blank');
            },
          },
        ],
      },
    });
  };

  const handleSubmit = async () => {
    const { hasErrors, items: newItems } = checkItemErrors(
      items,
      orgMemberEmails,
      workspaceMemberEmails,
      manageMembersType
    );
    setItems(newItems);

    if (hasErrors) {
      return;
    }

    if (membersLimit || guestsLimit || clientsLimit) {
      if (orgRole !== OrgMemberRole.OWNER) {
        showNotOwnerLimitError();
        return;
      }
      if (hasCoupons) {
        return showAppsumoLimitPopup();
      }
    }

    const sortedItems = sortItemsByClientOrgRole(items);
    setIsSending(true);
    if (manageMembersType === ManageMembersType.PORTAL) {
      const invites = sortedItems.map((item) => {
        return {
          email: item.email,
          role: 'editor',
          orgRole: item.role,
          isFullAccess: item.accessMode === PermissionsAccessMode.FULL_ACCESS,
        };
      });
      await inviteToPortal({
        invites,
        workspaceId: context.workspaceId,
        portalUrl: context.portalUrl,
        portalId: context.portalId,
      });
      toast({
        title: t('manage-members.added_members_to_portal'),
      });
      setItems([getInitialItem(items.length, defaultRole, defaultAccessRole)]);
    } else if (manageMembersType === ManageMembersType.PORTAL_NODE) {
      const itemPermissions = menuItem?.permissions;

      if (itemPermissions) {
        const invites = sortedItems.reduce((acc, item) => {
          if (acc.some((accItem) => accItem.email === item.email)) return acc;

          acc.push({
            email: item.email,
            orgRole: item.role,
            role: 'editor',
          });
          return acc;
        }, [] as Invite[]);
        const allowUserIds = itemPermissions.allowAll ? workspaceMemberUserIds : itemPermissions.allowUserIds;
        updatePermissions({
          permissions: { ...itemPermissions, allowAll: false, allowUserIds },
          targetId: menuItem.id,
          invites,
          workspaceId: context.workspaceId,
          portalUrl: context.portalUrl,
          fetchOrgMembers: true,
          orgId: context.orgId,
        })
          .then((result) => {
            const nodeName = menuItem ? getNodeNameByType(menuItem.type) : '';
            updateMenuItem({ ...menuItem, permissions: result.permissions });
            if (onUpdateMenuItemPermissions) {
              onUpdateMenuItemPermissions(menuItem.id, result.permissions);
            }
            toast({
              title: t('manage-members.change_access_for_the_item', {
                itemType: nodeName,
                menuItemName: menuItem.name,
              }),
            });
            setIsSending(false);
          })
          .catch(() => {
            toast({
              title: t('manage-members.an_error_inviting_user'),
            });
            setIsSending(false);
          });
        goToNodeAccessList();
      }
    }
    await getManageMembersData();
    setIsSending(false);
  };

  const goToNodeAccessList = () => {
    updateViewMode(ManageMembersView.NODE_ACCESS);
  };

  const handleCancel = () => {
    if (manageMembersType === ManageMembersType.PORTAL_NODE) {
      goToNodeAccessList();
    } else {
      closeModal();
    }
  };

  return (
    <>
      <div className="flex flex-col items-start h-full min-h-0">
        <div className="flex flex-col gap-2.5 mt-2.5 mb-2.5 overflow-y-auto h-auto w-full">
          {items.map((item, index) => (
            <Item
              key={item.id}
              index={index}
              lastAction={lastAction ?? ''}
              setLastAction={setLastAction}
              item={item}
              items={items}
              disabled={items.length === 1}
              hideClient={clientsDisabled}
              availablePortalAccessRoles={availablePortalAccessRoles}
              isOnlySharedAvailable={isOnlySharedAvailable && !availableConsoleOrgAccessRoles.includes(item.role)}
              onRemoveItem={handleRemoveItem}
              onUpdateItem={handleUpdateItem}
            />
          ))}
        </div>
        <button
          className="flex items-center justify-center font-bold text-sm h-9 text-cyan-900 p-2.5 hover:text-cyan-700 gap-2.5 active:text-cyan-700"
          onClick={handleAddItem}
        >
          <Icon icon={IconsMap.PLUS} className="w-5 h-5" />
          <span>{t('manage-members.invite_more')}</span>
        </button>
      </div>
      {manageMembersType === ManageMembersType.PORTAL_NODE && (
        <PortalNodeClientsInfoMessage title={title} type={transformInputStrings(type)} />
      )}
      <div className="flex">
        {showClientsError && <ClientsLimitMessage />}
        {showMembersError && <MembersLimitMessage />}
        {showGuestsError && <GuestsLimitMessage />}
        <div className="flex justify-end mt-2.5 gap-4 ml-auto">
          <Button type={ButtonType.STROKED} size={ButtonSize.XL} onClick={handleCancel}>
            {t('manage-members.cancel')}
          </Button>
          <Button
            size={ButtonSize.XL}
            type={ButtonType.FLAT}
            className="min-w-[172px] p-2"
            onClick={handleSubmit}
            disabled={sendDisabled}
          >
            {!isSending && <span>{t('manage-members.send_magic_link')}</span>}
            {isSending && (
              <span className="flex">
                {t('manage-members.sending_invitation')}{' '}
                <div className="flex ml-1">
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                </div>{' '}
                {''}
              </span>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}

function getInitialItem(
  index: number,
  defaultRole: OrgMemberRoleForInvite,
  accessMode?: PermissionsAccessMode
): InviteItemModel {
  const item: InviteItemModel = {
    id: `item-${Date.now()}-${index}`,
    role: defaultRole,
    email: '',
    isError: false,
    errorText: '',
  };

  if (accessMode !== undefined) {
    item.accessMode = accessMode;
  }
  return item;
}

function checkItemErrors(
  items: InviteItemModel[],
  organizationEmails: string[],
  workspaceEmails: string[],
  manageType: ManageMembersType
): { hasErrors: boolean; items: InviteItemModel[] } {
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  let hasErrors = false;
  const newItems = items.map((item) => {
    let errorText = '';
    if (!item.email.match(emailRegex)) {
      errorText = 'Incorrect email address';
      hasErrors = true;
    } else {
      if (manageType === ManageMembersType.PORTAL && workspaceEmails.length) {
        if (workspaceEmails.includes(item.email)) {
          errorText = 'Member already in portal';
          hasErrors = true;
        }
      } else if (organizationEmails.length) {
        if (organizationEmails.includes(item.email)) {
          errorText = 'Member already in organization';
          hasErrors = true;
        }
      }
    }
    return { ...item, isError: !!errorText, errorText: errorText };
  });

  return { hasErrors, items: newItems };
}

function sortItemsByClientOrgRole(invites: InviteItemModel[]) {
  return invites.sort((a, b) => {
    if (a.role === OrgMemberRole.CLIENT && b.role !== OrgMemberRole.CLIENT) {
      return -1;
    }
    if (a.role !== OrgMemberRole.CLIENT && b.role === OrgMemberRole.CLIENT) {
      return 1;
    }
    return 0;
  });
}

export default InviteList;
